/**
 * Blocking dialogs — separate from non-blocking notify.* (spec: NOTIFICATION_CENTER_AND_DIALOGS_SPEC.md §2, §8).
 * @see docs/RICH_NOTIFICATION_AND_DIALOG_TRUST_CONTRACT.md for `body` (ReactNode) trust rules.
 *
 * POLICY: Concurrent dialog.acknowledge / confirm / destructiveConfirm calls are queued (FIFO).
 * The next dialog opens only after the current Promise settles. This avoids stacked modals and
 * lost resolve functions.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

const AppDialogContext = createContext(null);

/**
 * @typedef {Object} AppDialogOptions
 * @property {React.ReactNode} [title]
 * @property {boolean} [hideTitle=false] — When true, suppresses title row entirely.
 * @property {React.ReactNode} [body]
 * @property {string} [dialogKey] — Optional dedupe key for queued dialogs.
 * @property {string} [confirmLabel] — Defaults: acknowledge → "OK"; confirm → "Confirm"; destructiveConfirm → "Delete"
 * @property {string} [cancelLabel] — Defaults to "Cancel" for confirm variants
 * @property {string} [paperBgColor] — Optional dialog surface background color
 * @property {number|string|object} [paperBorderRadius=2] — Optional dialog corner radius.
 * @property {'xs'|'sm'|'md'|'lg'|'xl'|false} [dialogMaxWidth='sm'] — Optional MUI dialog maxWidth.
 * @property {boolean} [titleDivider=true] — Whether to show a divider below title.
 * @property {import("@mui/material").SxProps} [contentSx] — Optional sx overrides for DialogContent.
 * @property {import("@mui/material").SxProps} [actionsSx] — Optional sx overrides for DialogActions.
 * @property {'default'|'status'} [appearance='default'] — Optional visual preset that matches StatusModal patterns.
 * @property {'contained'|'outlined'|'text'} [confirmButtonVariant='contained'] — Optional variant for confirm action.
 * @property {'contained'|'outlined'|'text'} [cancelButtonVariant='outlined'] — Optional variant for cancel action.
 * @property {'inherit'|'primary'|'secondary'|'error'|'info'|'success'|'warning'} [confirmButtonColor='primary'] — Optional color for confirm action.
 * @property {'inherit'|'primary'|'secondary'|'error'|'info'|'success'|'warning'} [cancelButtonColor='inherit'] — Optional color for cancel action.
 * @property {boolean} [ackFullWidth=true] — For acknowledge dialogs, render primary button full width.
 * @property {'stretch'|'flex-end'|'center'} [ackActionsAlign='stretch'] — For acknowledge dialogs, controls actions alignment.
 * @property {import("@mui/material").SxProps} [ackButtonSx] — Optional sx overrides for acknowledge primary button.
 */

/**
 * @typedef {Object} DialogQueueEntry
 * @property {'ack'|'confirm'} variant
 * @property {AppDialogOptions} options
 * @property {boolean} [destructive]
 * @property {(value?: unknown) => void} resolve
 */

function AppDialogHost({ active, onFinish }) {
  const open = active != null;
  const variant = active?.variant ?? "ack";
  const destructive = Boolean(active?.destructive);
  const options = active?.options ?? {};
  const title = options.title ?? "";
  const hideTitle = Boolean(options.hideTitle);
  const legacyBlockedDistributeTitle =
    typeof title === "string" && title.trim().toLowerCase() === "cannot distribute property";
  const hasTitle = !hideTitle && !legacyBlockedDistributeTitle && Boolean(title);
  const body = options.body ?? "";
  const paperBgColor = options.paperBgColor;
  const paperBorderRadius = options.paperBorderRadius ?? 2;
  const dialogMaxWidth = options.dialogMaxWidth ?? "sm";
  const titleDivider = options.titleDivider ?? true;
  const appearance = options.appearance ?? "default";
  const isStatusAppearance = appearance === "status";
  const contentSx = options.contentSx ?? {};
  const actionsSx = options.actionsSx ?? {};
  const confirmButtonVariant = options.confirmButtonVariant ?? "contained";
  const cancelButtonVariant = options.cancelButtonVariant ?? "outlined";
  const confirmButtonColor = options.confirmButtonColor ?? "primary";
  const cancelButtonColor = options.cancelButtonColor ?? "inherit";
  const ackFullWidth = options.ackFullWidth ?? true;
  const ackActionsAlign = options.ackActionsAlign ?? "stretch";
  const ackButtonSx = options.ackButtonSx ?? {};
  const confirmLabel =
    options.confirmLabel ??
    (variant === "ack" ? "OK" : destructive ? "Delete" : "Confirm");
  const cancelLabel = options.cancelLabel ?? "Cancel";
  const hasBody =
    typeof body === "string"
      ? body.trim() !== ""
      : body !== null && body !== undefined && body !== false;
  const isBlankAcknowledge = variant === "ack" && !hasTitle && !hasBody;

  useEffect(() => {
    if (!open || !isBlankAcknowledge) return;
    onFinish(undefined);
  }, [isBlankAcknowledge, onFinish, open]);

  const handleClose = useCallback(
    (_event, reason) => {
      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        if (variant === "confirm") {
          onFinish(false);
        } else {
          onFinish(undefined);
        }
      }
    },
    [onFinish, variant]
  );

  const handleCancel = useCallback(() => {
    if (variant === "confirm") {
      onFinish(false);
    } else {
      onFinish(undefined);
    }
  }, [onFinish, variant]);

  const handleConfirm = useCallback(() => {
    if (variant === "confirm") {
      onFinish(true);
    } else {
      onFinish(undefined);
    }
  }, [onFinish, variant]);

  return (
    isBlankAcknowledge ? null : (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={dialogMaxWidth}
      fullWidth
      aria-labelledby={hasTitle ? "app-dialog-title" : undefined}
      aria-describedby="app-dialog-description"
      slotProps={{
        paper: {
          sx: {
            borderRadius: isStatusAppearance ? "16px" : paperBorderRadius,
            border: "1px solid rgba(0, 0, 0, 0.06)",
            boxShadow: "0 12px 40px rgba(15, 23, 42, 0.12)",
            ...(paperBgColor ? { bgcolor: paperBgColor } : {}),
          },
        },
      }}
    >
      {hasTitle && (
        <DialogTitle
          id="app-dialog-title"
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: "1.125rem",
            px: 3,
            pt: isStatusAppearance ? 3 : 2.5,
            pb: isStatusAppearance ? 1 : 2,
            textAlign: isStatusAppearance ? "center" : "left",
            ...(titleDivider && !isStatusAppearance ? { borderBottom: "1px solid rgba(0, 0, 0, 0.06)" } : {}),
          }}
        >
          {title}
        </DialogTitle>
      )}
      <DialogContent
        id="app-dialog-description"
        sx={{
          px: 3,
          pt: hasTitle ? (isStatusAppearance ? 1.5 : 3) : 3.25,
          pb: 1.5,
          textAlign: isStatusAppearance ? "center" : "left",
          ...contentSx,
        }}
      >
        {typeof body === "string" ? (
          <DialogContentText
            component="div"
            sx={{
              fontSize: "0.9375rem",
              lineHeight: 1.6,
              color: "var(--foreground, #2D3748)",
              m: 0,
            }}
          >
            {body}
          </DialogContentText>
        ) : (
          <Box sx={{ fontSize: "0.9375rem", lineHeight: 1.6, color: "var(--foreground, #2D3748)" }}>{body}</Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 1.75,
          gap: 1,
          flexWrap: "wrap",
          ...(variant === "confirm" && destructive
            ? { justifyContent: "flex-end" }
            : variant === "confirm"
              ? { justifyContent: isStatusAppearance ? "center" : "stretch" }
              : { justifyContent: ackActionsAlign }),
          ...actionsSx,
        }}
      >
        {variant === "confirm" ? (
          destructive ? (
            <>
              <Button variant="text" onClick={handleCancel} color="inherit" sx={{ textTransform: "none" }}>
                {cancelLabel}
              </Button>
              <Button variant="contained" onClick={handleConfirm} color="error" sx={{ textTransform: "none" }}>
                {confirmLabel}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant={cancelButtonVariant}
                onClick={handleCancel}
                color={cancelButtonColor}
                sx={{
                  flex: 1,
                  textTransform: "none",
                  borderRadius: isStatusAppearance ? "8px" : undefined,
                  minHeight: isStatusAppearance ? 36 : undefined,
                  maxWidth: isStatusAppearance ? 220 : undefined,
                }}
              >
                {cancelLabel}
              </Button>
              <Button
                variant={confirmButtonVariant}
                onClick={handleConfirm}
                color={confirmButtonColor}
                sx={{
                  flex: 1,
                  textTransform: "none",
                  borderRadius: isStatusAppearance ? "8px" : undefined,
                  minHeight: isStatusAppearance ? 36 : undefined,
                  maxWidth: isStatusAppearance ? 220 : undefined,
                }}
              >
                {confirmLabel}
              </Button>
            </>
          )
        ) : (
          <Button
            variant="contained"
            onClick={handleConfirm}
            color="primary"
            fullWidth={ackFullWidth}
            sx={{
              textTransform: "none",
              ...(ackFullWidth
                ? {}
                : {
                    minWidth: 120,
                    px: 3,
                    py: 1,
                    borderRadius: "8px",
                  }),
              ...ackButtonSx,
            }}
          >
            {confirmLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    )
  );
}

export function AppDialogProvider({ children }) {
  /** @type {React.MutableRefObject<DialogQueueEntry[]>} */
  const queueRef = useRef([]);
  const [active, setActive] = useState(/** @type {DialogQueueEntry | null} */ (null));

  const enqueue = useCallback((entry) => {
    setActive((cur) => {
      const key = entry?.options?.dialogKey;
      if (typeof key === "string" && key.trim() !== "") {
        const normalizedKey = key.trim();
        const currentKey = cur?.options?.dialogKey;
        if (currentKey === normalizedKey) {
          entry?.resolve?.(entry?.variant === "confirm" ? false : undefined);
          return cur;
        }
        const hasQueuedDuplicate = queueRef.current.some((queued) => queued?.options?.dialogKey === normalizedKey);
        if (hasQueuedDuplicate) {
          entry?.resolve?.(entry?.variant === "confirm" ? false : undefined);
          return cur;
        }
      }
      if (cur == null) return entry;
      queueRef.current.push(entry);
      return cur;
    });
  }, []);

  const acknowledge = useCallback(
    (/** @type {AppDialogOptions} */ options) =>
      new Promise((resolve) => {
        enqueue({ variant: "ack", options, resolve });
      }),
    [enqueue]
  );

  const confirm = useCallback(
    (/** @type {AppDialogOptions} */ options) =>
      new Promise((resolve) => {
        enqueue({ variant: "confirm", options, destructive: false, resolve });
      }),
    [enqueue]
  );

  const destructiveConfirm = useCallback(
    (/** @type {AppDialogOptions} */ options) =>
      new Promise((resolve) => {
        enqueue({ variant: "confirm", options, destructive: true, resolve });
      }),
    [enqueue]
  );

  const finish = useCallback((result) => {
    setActive((cur) => {
      if (cur?.resolve) {
        cur.resolve(result);
      }
      const closedKey = cur?.options?.dialogKey;
      let next = null;

      while (queueRef.current.length > 0) {
        const candidate = queueRef.current.shift();
        if (!candidate) continue;
        const candidateKey = candidate?.options?.dialogKey;
        if (closedKey && candidateKey === closedKey) {
          candidate.resolve?.(candidate.variant === "confirm" ? false : undefined);
          continue;
        }
        next = candidate;
        break;
      }

      if (next?.options?.dialogKey) {
        const nextKey = next.options.dialogKey;
        const compactedQueue = [];
        for (const queued of queueRef.current) {
          if (queued?.options?.dialogKey === nextKey) {
            queued.resolve?.(queued.variant === "confirm" ? false : undefined);
            continue;
          }
          compactedQueue.push(queued);
        }
        queueRef.current = compactedQueue;
      }

      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      acknowledge,
      confirm,
      destructiveConfirm,
    }),
    [acknowledge, confirm, destructiveConfirm]
  );

  return (
    <AppDialogContext.Provider value={value}>
      {children}
      <AppDialogHost active={active} onFinish={finish} />
    </AppDialogContext.Provider>
  );
}

/**
 * @returns {{
 *   acknowledge: (options: AppDialogOptions) => Promise<void>,
 *   confirm: (options: AppDialogOptions) => Promise<boolean>,
 *   destructiveConfirm: (options: AppDialogOptions) => Promise<boolean>,
 * }}
 */
export function useAppDialog() {
  const ctx = useContext(AppDialogContext);
  if (!ctx) {
    throw new Error("useAppDialog must be used within AppDialogProvider");
  }
  return ctx;
}
